import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import ComponentCard from '../../common/components/cards/ComponentCard';
import {
  FormDatePickerField,
  FormGroupCheckboxField,
  FormInputField,
  FormSelectField,
} from '../../common/components/formFields';

import { constantToSelect } from '../../common/helpers';
import { CLEAR_USER_SEARCH_STATE } from '../UsersReducer';
import validationSchema from './UsersSearch.schema';
import { AgreeOptions } from '../../constants/AgreeOptions';

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  tradeName: '',
  registeredDateFrom: '',
  registeredDateTo: '',
  role: [],
  mailchimpId: '',
  phoneMarketingAllowed: null,
  emailMarketingAllowed: null,
  smsMarketingAllowed: null,
  postMarketingAllowed: null,
};

const agreeOptions = constantToSelect(AgreeOptions);
const rolesOptions = constantToSelect({
  BUYER: 'Buyer',
  SELLER: 'Seller',
});

function DealerUserSearch({ onSearch, dealerName }) {
  const dispatch = useDispatch();
  const formRef = useRef();
  const previousSearchState = useSelector((state) => state.users.get('searchState'));
  const [reloadDatePickerKey, setReloadDatePickerKey] = useState(false);

  useEffect(() => {
    if (dealerName && formRef.current) {
      const currentValues = formRef.current.values;
      formRef.current.setValues({
        ...currentValues,
        tradeName: dealerName || currentValues.tradeName,
      });
    }
  }, [dealerName]);

  useEffect(() => {
    if (previousSearchState?.searchParams) {
      formRef.current.setValues(previousSearchState?.searchParams);
    }
  }, [previousSearchState]);

  const handleClear = (resetForm, submitForm) => {
    dispatch({ type: CLEAR_USER_SEARCH_STATE });
    setReloadDatePickerKey((prevState) => !prevState);
    resetForm();

    if (dealerName) {
      const preservedValues = {
        tradeName: dealerName || '',
      };

      formRef.current.setValues({
        ...initialValues,
        ...preservedValues,
      });
    }

    submitForm();
  };

  return (
    <Row>
      <Col md="12">
        <ComponentCard title="Search User" isCollapsible>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSearch}
            validateOnBlur
          >
            {({ resetForm, submitForm }) => (
              <Form noValidate>
                <Row>
                  <Col md="4">
                    <FormInputField name="firstName" label="First name" />
                  </Col>
                  <Col md="4">
                    <FormInputField name="lastName" label="Last name" />
                  </Col>
                  <Col md="4">
                    <FormInputField name="email" label="Email" />
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <FormInputField name="tradeName" label="Dealer" disabled={!!dealerName} />
                  </Col>
                  <Col md="4">
                    <FormDatePickerField
                      name="registeredDateFrom"
                      label="Registered Date From"
                      key={reloadDatePickerKey}
                    />
                  </Col>
                  <Col md="4">
                    <FormDatePickerField
                      name="registeredDateTo"
                      label="Registerd Date To"
                      isUpperRange
                      key={reloadDatePickerKey}
                    />
                  </Col>
                </Row>
                <Row>
                  <FormGroup className="mt-2 mb-2">
                    <FormLabel>Marketing preferences</FormLabel>
                  </FormGroup>
                </Row>
                <Row>
                  <Col md="2">
                    <FormSelectField
                      name="phoneMarketingAllowed"
                      label="Phone"
                      options={agreeOptions}
                    />{' '}
                  </Col>
                  <Col md="2" className="m-0">
                    <FormSelectField
                      name="emailMarketingAllowed"
                      label="Email"
                      options={agreeOptions}
                    />
                  </Col>
                  <Col md="2">
                    <FormSelectField
                      name="smsMarketingAllowed"
                      label="SMS"
                      options={agreeOptions}
                    />
                  </Col>
                  <Col md="2">
                    <FormSelectField
                      name="postMarketingAllowed"
                      label="Post"
                      options={agreeOptions}
                    />
                  </Col>
                  <Col md="4">
                    <FormInputField name="county" label="County" />
                  </Col>
                </Row>
                <Row>
                  <FormGroup className="mb-1">
                    <FormLabel>Role</FormLabel>
                  </FormGroup>
                </Row>
                <Row>
                  <Col md="4" className="mb-3">
                    <FormGroupCheckboxField
                      inline
                      name="role"
                      label="Role"
                      checkboxes={rolesOptions}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <Button
                      className="mt-4 float-end ms-2 rounded-pill btn-primary"
                      type="submit"
                    >
                      Search
                    </Button>
                    <Button
                      className="mt-4 float-end rounded-pill btn-soft-primary"
                      type="button"
                      onClick={() => handleClear(resetForm, submitForm)}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </ComponentCard>
      </Col>
    </Row>
  );
}

DealerUserSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
  dealerName: PropTypes.string,
};

DealerUserSearch.defaultProps = {
  dealerName: null,
};

export default DealerUserSearch;
